import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import AutoFixHighOutlined from "@mui/icons-material/AutoFixHighOutlined";
import { GlobalContext } from "../../context/GlobalContext";
import { openExploreRecreateInVideoStudio } from "./exploreIdeasBridge";
import RecreateProductForm from "./RecreateProductForm";

const PAGE_SIZE = 10;

function recreateButtonSx(theme) {
  return {
    borderRadius: 2,
    fontWeight: 700,
    bgcolor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.45)}`,
    "&:hover": {
      bgcolor: theme.palette.primary.dark,
      color: theme.palette.primary.contrastText,
      boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.55)}`,
    },
    "&.Mui-disabled": {
      bgcolor: alpha(theme.palette.primary.main, 0.4),
      color: alpha(theme.palette.primary.contrastText, 0.85),
    },
  };
}

function TemplateCard({ item, onOpen }) {
  const theme = useTheme();
  const rootRef = React.useRef(null);
  const videoRef = React.useRef(null);
  const [inView, setInView] = React.useState(false);
  const [hovered, setHovered] = React.useState(false);
  const [posterFailed, setPosterFailed] = React.useState(false);

  const hasPoster = Boolean(item.poster) && !posterFailed;
  const showPoster = hasPoster && !hovered;

  React.useEffect(() => {
    const node = rootRef.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "160px 0px", threshold: 0 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const primeVideoFrame = React.useCallback(() => {
    const el = videoRef.current;
    if (!el) return;
    try {
      if (el.readyState >= 1) {
        el.currentTime = 0.05;
      }
    } catch {
      // ignore seek errors before metadata is ready
    }
  }, []);

  const handleEnter = () => {
    setHovered(true);
    requestAnimationFrame(() => {
      const el = videoRef.current;
      if (!el) return;
      el.currentTime = 0;
      el.play().catch(() => {});
    });
  };

  const handleLeave = () => {
    setHovered(false);
    const el = videoRef.current;
    if (!el) return;
    el.pause();
    primeVideoFrame();
  };

  return (
    <Box
      ref={rootRef}
      onClick={() => onOpen(item)}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onFocus={handleEnter}
      onBlur={handleLeave}
      tabIndex={0}
      role="button"
      aria-label={item.title || "Video template"}
      sx={{
        position: "relative",
        borderRadius: 3,
        overflow: "hidden",
        cursor: "pointer",
        aspectRatio: "9 / 16",
        bgcolor: alpha(theme.palette.text.primary, 0.04),
        border: "1px solid",
        borderColor: "divider",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        "&:hover, &:focus-visible": {
          transform: "translateY(-2px)",
          boxShadow: `0 12px 28px ${alpha(theme.palette.common.black, 0.12)}`,
          outline: "none",
        },
      }}
    >
      {showPoster && (
        <Box
          component="img"
          src={item.poster}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setPosterFailed(true)}
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
      )}

      {inView && item.video && (
        <Box
          component="video"
          ref={videoRef}
          src={item.video}
          muted
          playsInline
          loop
          preload={hasPoster && !hovered ? "none" : "metadata"}
          onLoadedMetadata={primeVideoFrame}
          onLoadedData={primeVideoFrame}
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
            opacity: showPoster ? 0 : 1,
            transition: "opacity 0.2s ease",
            pointerEvents: "none",
          }}
        />
      )}
    </Box>
  );
}

function AdDetailsModal({ open, item, onClose, lang }) {
  const theme = useTheme();
  const { showSnack } = React.useContext(GlobalContext);
  const [step, setStep] = React.useState("details");
  const [submitting, setSubmitting] = React.useState(false);
  const [genError, setGenError] = React.useState(null);

  React.useEffect(() => {
    if (!open) {
      setStep("details");
      setSubmitting(false);
      setGenError(null);
    }
  }, [open, item?.id]);

  if (!item) return null;

  const handleRecreateSubmit = async ({ characterFile, productFile, productDescription, dialog }) => {
    setSubmitting(true);
    setGenError(null);
    try {
      await openExploreRecreateInVideoStudio(item, {
        characterFile,
        productFile,
        productDescription,
        dialog,
      });
    } catch (e) {
      const msg = e?.message || "Could not open Video Studio.";
      setGenError(msg);
      showSnack?.(msg, false);
    } finally {
      setSubmitting(false);
    }
  };

  const modalCloseDisabled = submitting;

  return (
    <Dialog
      open={open}
      onClose={modalCloseDisabled ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, overflow: "hidden" },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontWeight: 800,
          pb: 1,
        }}
      >
        {lang?.exploreIdeasModalTitle || "Ad Details"}
        <IconButton onClick={onClose} aria-label="Close" size="small" disabled={modalCloseDisabled}>
          <CloseOutlined />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 1, pb: 3 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={3}
          alignItems={{ xs: "stretch", md: "flex-start" }}
        >
          <Box
            sx={{
              flex: { md: "0 0 280px" },
              width: { xs: "100%", md: 280 },
              mx: { xs: "auto", md: 0 },
              position: "relative",
            }}
          >
            <Box
              component="video"
              src={item.video}
              controls
              playsInline
              poster={item.poster || undefined}
              preload="metadata"
              sx={{
                width: "100%",
                aspectRatio: "9 / 16",
                borderRadius: 2,
                bgcolor: alpha(theme.palette.common.black, 0.06),
                objectFit: "cover",
                display: "block",
              }}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, pt: { md: step === "details" ? 0.5 : 0 } }}>
            {step === "details" && (
              <>
                <Typography variant="h6" fontWeight={800} gutterBottom>
                  {item.title}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  {item.description}
                </Typography>
              </>
            )}
            {step === "recreate" && (
              <RecreateProductForm
                lang={lang}
                submitting={submitting}
                error={genError}
                onBack={() => {
                  setGenError(null);
                  setStep("details");
                }}
                onSubmit={handleRecreateSubmit}
              />
            )}
          </Box>
        </Stack>
      </DialogContent>
      {step === "details" && (
        <DialogActions
          sx={{
            px: 3,
            py: 2.5,
            gap: 1.5,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box sx={{ flex: 1 }} />
          <Button onClick={onClose} variant="outlined" color="inherit" sx={{ borderRadius: 2, minWidth: 96 }}>
            {lang?.close || "Close"}
          </Button>
          <Button
            onClick={() => setStep("recreate")}
            variant="contained"
            color="primary"
            startIcon={<AutoFixHighOutlined />}
            sx={recreateButtonSx(theme)}
          >
            {lang?.exploreIdeasRecreate || "Recreate"}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}

export default function ExploreIdeas({ lang }) {
  const theme = useTheme();
  const { hitAxios } = React.useContext(GlobalContext);
  const [categories, setCategories] = React.useState([]);
  const [items, setItems] = React.useState([]);
  const [activeCategory, setActiveCategory] = React.useState("All");
  const [loading, setLoading] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [err, setErr] = React.useState(null);
  const [selected, setSelected] = React.useState(null);
  const [hasMore, setHasMore] = React.useState(false);
  const pageRef = React.useRef(1);
  const loadMoreRef = React.useRef(null);
  const fetchingRef = React.useRef(false);

  const loadCategories = React.useCallback(async () => {
    const res = await hitAxios({
      path: "/api/explore-ideas/categories",
      post: false,
      admin: false,
    });
    if (!res.data?.success) throw new Error(res.data?.msg || "Failed to load categories");
    setCategories(Array.isArray(res.data.data) ? res.data.data : []);
  }, [hitAxios]);

  const fetchTemplates = React.useCallback(
    async ({ category, page, append }) => {
      const params = new URLSearchParams();
      if (category && category !== "All") params.set("category", category);
      params.set("page", String(page));
      params.set("limit", String(PAGE_SIZE));

      const res = await hitAxios({
        path: `/api/explore-ideas/templates?${params.toString()}`,
        post: false,
        admin: false,
      });
      if (!res.data?.success) throw new Error(res.data?.msg || "Failed to load templates");

      const payload = res.data.data || {};
      const nextItems = Array.isArray(payload.items) ? payload.items : [];
      const pagination = payload.pagination || {};

      setItems((prev) => (append ? [...prev, ...nextItems] : nextItems));
      setHasMore(Boolean(pagination.has_more));
      pageRef.current = page;
    },
    [hitAxios],
  );

  const loadInitial = React.useCallback(
    async (category) => {
      pageRef.current = 1;
      setLoading(true);
      setErr(null);
      try {
        await fetchTemplates({ category, page: 1, append: false });
      } catch (e) {
        setItems([]);
        setHasMore(false);
        setErr(e?.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    },
    [fetchTemplates],
  );

  const loadMore = React.useCallback(async () => {
    if (fetchingRef.current || loading || loadingMore || !hasMore) return;
    fetchingRef.current = true;
    setLoadingMore(true);
    setErr(null);
    try {
      await fetchTemplates({
        category: activeCategory,
        page: pageRef.current + 1,
        append: true,
      });
    } catch (e) {
      setErr(e?.message || "Something went wrong");
    } finally {
      setLoadingMore(false);
      fetchingRef.current = false;
    }
  }, [activeCategory, fetchTemplates, hasMore, loading, loadingMore]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await loadCategories();
        if (!cancelled) await loadInitial("All");
      } catch (e) {
        if (!cancelled) setErr(e?.message || "Something went wrong");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadCategories, loadInitial]);

  React.useEffect(() => {
    const node = loadMoreRef.current;
    if (!node || !hasMore) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          loadMore();
        }
      },
      { root: null, rootMargin: "240px 0px", threshold: 0 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadMore, items.length]);

  const handleCategory = async (category) => {
    setActiveCategory(category);
    await loadInitial(category);
  };

  const filterChips = ["All", ...categories];

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 }, maxWidth: 1400, mx: "auto" }}>
      <Typography
        variant="h4"
        align="center"
        sx={{ fontWeight: 500, mb: 3, color: "text.primary", lineHeight: 1.3 }}
      >
        {(lang?.exploreIdeasHeroPrefix || "Create AI ads with our") + " "}
        <Box component="span" sx={{ fontWeight: 800, color: theme.palette.primary.main }}>
          {lang?.exploreIdeasNav || "Explore Ideas"}
        </Box>
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          justifyContent: "center",
          mb: 3,
        }}
      >
        {filterChips.map((cat) => {
          const selected = activeCategory === cat;
          return (
            <Chip
              key={cat}
              label={cat}
              onClick={() => handleCategory(cat)}
              disabled={loading && activeCategory === cat}
              sx={{
                borderRadius: 999,
                fontWeight: 600,
                px: 0.5,
                bgcolor: selected ? "#111827" : "background.paper",
                color: selected ? "#fff" : "text.primary",
                border: "1px solid",
                borderColor: selected ? "#111827" : "divider",
                "&:hover": {
                  bgcolor: selected ? "#111827" : alpha(theme.palette.primary.main, 0.06),
                },
              }}
            />
          );
        })}
      </Box>

      {err && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {err}
        </Alert>
      )}

      {loading && !items.length ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : !items.length ? (
        <Alert severity="info">{lang?.exploreIdeasEmpty || "No templates found for this category."}</Alert>
      ) : (
        <>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, minmax(0, 1fr))",
                sm: "repeat(3, minmax(0, 1fr))",
                md: "repeat(4, minmax(0, 1fr))",
                lg: "repeat(5, minmax(0, 1fr))",
                xl: "repeat(6, minmax(0, 1fr))",
              },
              gap: { xs: 1.5, md: 2 },
            }}
          >
            {items.map((item) => (
              <TemplateCard key={item.id} item={item} onOpen={setSelected} />
            ))}
          </Box>

          <Box
            ref={loadMoreRef}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 72,
              py: 2,
            }}
          >
            {loadingMore && <CircularProgress size={28} />}
            {!loadingMore && hasMore && (
              <Typography variant="body2" color="text.secondary">
                {lang?.exploreIdeasScrollHint || "Scroll for more ideas"}
              </Typography>
            )}
          </Box>
        </>
      )}

      <AdDetailsModal
        open={Boolean(selected)}
        item={selected}
        onClose={() => setSelected(null)}
        lang={lang}
      />
    </Box>
  );
}
